<template>
  <div :class="['c-unit', `c-unit--${unitSlug}`]">
    <div class="c-unit__header u-px">
      <div class="c-breadcrumbs__container">
        <Breadcrumbs
          :crumbs="crumbs"
          class="c-unit__header-breadcrumbs u-py u-px"
        />
      </div>
      <UnitHeader
        v-if="isTeacher"
        class="c-unit__header-component"
        :title="unit.name"
        :video="unit.video"
        :index="unitIndex"
        :cover="unit.cover"
        :duration="unit.duration"
        :description="unit.description"
      />

      <div
        v-else
        class="c-unit__header-component c-unit-header c-unit__header-component--student"
      >
        <VHeading v-if="unitIndex" header="h1" class="c-unit-header__title">
          <span class="c-unit-header__title-index">{{ unitIndex }}</span>
          {{ unit.name }}
        </VHeading>
        <div class="c-unit-header__wrapper">
          <div class="c-unit-header__col">
            <picture v-if="unit.cover" class="c-unit-header__picture">
              <img :src="unit.cover.src" />
            </picture>
          </div>
          <div class="c-unit-header__col">
            <div
              v-if="unit.description"
              v-md-html="unit.description"
              class="c-unit-header__desc"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="c-unit-overview">
      <div
        v-for="lesson in lessons"
        :key="lesson._id"
        :class="`c-lesson-section c-unit-overview__lesson u-px c-unit-overview__lesson--${lesson.slug}`"
      >
        <a
          :href="`/plan/lesson/${lesson.slug}`"
          class="c-lesson-header__title-link"
        >
          <VHeading header="h2" class="c-lesson-header__title">
            {{ lesson.name }}
          </VHeading>
          <i class="c-lesson-header__icon app-icon-chevron_right"></i>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { fetchWithAuth } from '@/api'
import { fetchUnitLessons } from '@/services/strapi'
import {
  addCloudinaryUrlParams,
  parseLearningElements,
  mergeUnits,
} from '@/utils'
import Breadcrumbs from '@forlagshuset/v-breadcrumbs'
import VHeading from '@forlagshuset/v-heading'
// import UnitOverview from '@/components/UnitOverview'
import { docTitleHandler } from '@/services/title'
import UnitHeader from '@/components/UnitHeader'

export default {
  components: {
    Breadcrumbs,
    // UnitOverview,
    VHeading,
    UnitHeader,
  },

  setup(props, ctx) {
    const crumbs = ref([])
    const unit = ref({})
    const unitSlug = computed(
      () => ctx.root.$options.router.history.current.params.slug,
    )
    const parentSlug = computed(
      () => ctx.root.$options.router.history.current.params.pslug,
    )
    const parentName = ref('')
    const childrenUnits = ref([])
    const unitConcepts = ref([])
    const singleConcept = ref(null)
    const relatedConcept = ref([])
    const unitIndex = ref(0)
    const lessons = ref([])
    const conceptsFilters = ref([])
    const filteredConcepts = computed(() => {
      if (!conceptsFilters.value.length) return unitConcepts.value

      const filtered = unitConcepts.value.filter(
        (uc) =>
          uc.tags.filter((tag) => conceptsFilters.value.indexOf(tag) !== -1)
            .length === conceptsFilters.value.length,
      )
      const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name, 'no'))

      return sorted
    })

    const isTeacher = computed(
      () => ctx.root.$store.getters['appConfig/isTeacher'],
    )

    onMounted(async () => {
      await ctx.root.$store.dispatch('content/setUnits')

      crumbs.value.push({
        type: 'plan',
        name: ctx.root.$i18n.t('PLAN_NAME'),
        slug: '',
      })

      parentSlug.value
        ? (parentName.value = ctx.root.$store.getters['content/getUnitBySlug'](
            parentSlug.value,
          ).name)
        : ''
      const units = ctx.root.$store.getters['content/getUnits']
      unit.value = ctx.root.$store.getters['content/getUnitBySlug'](
        unitSlug.value,
      )
      units.forEach((u) => {
        if (u.parent && u.parent.id === unit.value.id) {
          childrenUnits.value.push(u)
        }
      })
      //concept find for Puls
      if (unit.value.tags.length) {
        const fetchedTag = await (
          await fetchWithAuth('tag-types/' + unit.value.tags[0].id)
        ).json()
        unitConcepts.value = fetchedTag.concepts.sort((a, b) =>
          a.name.localeCompare(b.name, 'no'),
        )
      }

      //find index of the unit
      const unitList = mergeUnits(units)

      unitList.forEach((parent, i) => {
        if (unit.value.parent && parent.id === unit.value.parent.id) {
          parent.units.forEach((child, j) => {
            if (child.slug === unitSlug.value) {
              unitIndex.value = j + 1
            }
          })
        } else {
          if (parent.slug === unitSlug.value) {
            unitIndex.value = i + 1
          }
        }
      })

      setCrumbs(unit.value)
      docTitleHandler(unit.value.name)

      if (unit.value.duration && unit.value.duration.minValue) {
        unit.value.duration.durationUnit = ctx.root.$t(
          unit.value.duration.durationUnit.toUpperCase(),
        )
      }

      if (
        !ctx.root.$store.getters['content/hasStoredUnitLessons'](
          unit.value.slug,
        )
      ) {
        ctx.root.$store.dispatch(
          'content/setLessons',
          await fetchUnitLessons(unit.value.slug),
        )
      }

      const orderedLessons = JSON.parse(
        JSON.stringify(
          ctx.root.$store.getters['content/getUnitLessons'](unit.value.id),
        ),
      )

      if (unit.value.cover && unit.value.cover.image_type) {
        const coverURL = unit.value.cover.image_type.file.url
        const param = 'w_640,c_scale' // Should be adjusted per project need (styling overview list for example)
        unit.value.cover.src = addCloudinaryUrlParams(coverURL, param)
      }

      //get ordered lessons
      orderedLessons.forEach((lesson) => {
        lessons.value.push(lesson)
      })

      lessons.value.forEach((lesson) => {
        lesson.parsedLES = parseLearningElements(lesson.learningElementSection)
        lesson.parsedScenario = parseLearningElements(lesson.scenario)
      })
    })

    const onFilterUpdate = (selectedFilters) => {
      conceptsFilters.value = selectedFilters
    }

    function setCrumbs(unit) {
      if (parentSlug.value) {
        crumbs.value.push({
          type: 'unit',
          name: parentName.value,
          slug: parentSlug.value,
        })
      }

      crumbs.value.push({
        type: 'unit',
        name: unit.name,
        slug: unit.slug,
      })
    }

    function onShowConcept(concept) {
      singleConcept.value = concept
      relatedConcept.value = concept.related.length
        ? unitConcepts.value.filter((uc) => concept.related.indexOf(uc.id) > -1)
        : []
    }

    function onHideConcept() {
      singleConcept.value = null
      relatedConcept.value = []
    }

    return {
      crumbs,
      unit,
      unitSlug,
      isTeacher,
      childrenUnits,
      unitConcepts,
      singleConcept,
      onShowConcept,
      onHideConcept,
      unitIndex,
      filteredConcepts,
      onFilterUpdate,
      lessons,
      parentSlug,
      relatedConcept,
    }
  },
}
</script>

<style lang="scss">
.c-unit__filtering {
  margin: 0 1rem 2rem 1rem;
  display: flex;
  justify-content: space-between;
}
</style>
